import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { SocoHttpService, ApiResult } from '@app/base/soco';
import {
  EntityLookup, CompanyDetails, City, State, County, LocalDistrict,
  GasLoadMultiplier, QuestionsLkp, GasDeliveryPressure, GasLoadAppliance, ServiceCenter, GasRequestType, GasRequestTypeBcaCode,
  Messages, GasRequestTypeCode, GasRequestStatus, Type, DevelopmentStreetTypeCode, LookupItem, WorkRequestType,
  BannerMessages, BuildingType, UtitlityEasementTypeCode
} from '@app/core/models';
import { LookupsEnum } from '@app/base/core/models';
import { CountyDetailsModel } from '@app/shared/models/countydetails.model';


/* istanbul ignore next */
@Injectable({
  providedIn: 'root'
})
export class LookupsService {
  // Eagerly Loaded Lookup, loaded before app start to properly render UI.
  companies: CompanyDetails; // LDCs

  pleaseWaitMsg = 'Processing.';

  // Lazy loaded Common Lookups
  states: State[];
  counties: County[];
  directions: LookupItem[];
  streetTypes: LookupItem[];
  cities: LookupItem[];
  projectTypes: EntityLookup;
  contactTypes: EntityLookup;
  bcaStatus: EntityLookup[];
  developmentStatus: EntityLookup;
  constructionStatus: EntityLookup;
  gasQuestionnaires: QuestionsLkp[];
  gasRequestStatus: EntityLookup;
  gasRequestType: GasRequestTypeCode[];
  gasRequestTypeBcaCode: GasRequestTypeBcaCode[];
  bcaType: GasRequestType[];
  buildingTypes: BuildingType[];
  countyDetails: CountyDetailsModel ;
  messages: Messages[];
  developmentStreetType: DevelopmentStreetTypeCode[];
  UtilityEasementType: UtitlityEasementTypeCode[];
  WorkRequestType: WorkRequestType[];
  LotStatusTypes: LookupItem[];
  MarketerOfChoice: LookupItem[];
  MarketerOfChoiceUndecided: LookupItem[];
  serviceCenters: ServiceCenter[];
  bannerMessages: BannerMessages[];
  localDistrict: LocalDistrict[];


  // On-demand lookups, loaded based on LDC and Request Type
  loadType: GasLoadAppliance[];
  deliveryPressure: GasDeliveryPressure[];
  defaultResidentialPressure: EntityLookup;
  defaultCommercialPressure: EntityLookup;
  gasLoadMultiplier: GasLoadMultiplier[];
  wrLoadArmLkp: any[];
  county: County[];
  // On-demand service address lookups, loaded when user select a service address
  serviceState: State[];
  serviceCity: City[];
  serviceCounty: County[];
  meterDuplicate: Type;
  bcaDuplicate: GasRequestStatus;

  // On-demand mailing address lookups, loaded when user select a service address
  mailingCity: City[];
  mailingState: State[];

  // static phone type list, can be moved to api later if needed
  phoneNumberType: any[] = [{id: 1, itemName: 'MOBILE'}, {id: 2, itemName: 'WORK'}, {id: 3, itemName: 'HOME'}];
  contractRejectReason: any[];
  public commonLookupsLoaded = false;
  private commonLookupsSource = new BehaviorSubject<boolean>(false);
  public commonLookupsLoaded$ = this.commonLookupsSource.asObservable();

  private bcaLookupsSource = new BehaviorSubject<boolean>(false);
  public bcaLookupsLoaded$ = this.bcaLookupsSource.asObservable();
  private builderBcaLookupsSource = new BehaviorSubject<boolean>(false);
  public builderBcaLookupsLoaded$ = this.bcaLookupsSource.asObservable();

  private serviceAddressLookupsSource = new BehaviorSubject<boolean>(false);
  public serviceAddressLookupsLoaded$ = this.serviceAddressLookupsSource.asObservable();

  private mailingAddressLookupsSource = new BehaviorSubject<boolean>(false);
  public mailingAddressLookupsLoaded$ = this.mailingAddressLookupsSource.asObservable();

  constructor(private socoHttp: SocoHttpService) { }

  public loadCommonLookups() {
    this.commonLookupsLoaded = false;
    this.commonLookupsSource.next(false);
    this.fetchCommonLookups().subscribe(results => {
      this.states = results.data[LookupsEnum.States].lookupValues;
      this.projectTypes = results.data[LookupsEnum.ProjectTypes];
      this.contactTypes = results.data[LookupsEnum.ContactTypes];
      this.bcaStatus = results.data[LookupsEnum.BcaStatus].lookupValues;
      this.developmentStatus = results.data[LookupsEnum.DevelopmentStatus];
      this.cities = results.data[LookupsEnum.City].lookupValues;
      this.counties = results.data[LookupsEnum.County].lookupValues;
      this.constructionStatus = results.data[LookupsEnum.ConstructionStatus];
      this.gasQuestionnaires = results.data[LookupsEnum.GasQuestionnaires].lookupValues;
      this.gasRequestStatus = results.data[LookupsEnum.GasRequestStatus];
      this.gasRequestType = results.data[LookupsEnum.GasRequestType].lookupValues;
      this.gasRequestTypeBcaCode = results.data[LookupsEnum.GasRequestTypeBcaCode].lookupValues;
      this.bcaType = results.data[LookupsEnum.BCAType].lookupValues;
      this.messages = results.data[LookupsEnum.Messages].lookupValues;
      this.serviceCenters = results.data[LookupsEnum.ServiceCenter].lookupValues;
      this.WorkRequestType = results.data[LookupsEnum.WorkRequestType].lookupValues;
      this.LotStatusTypes = results.data[LookupsEnum.LotStatusType].lookupValues;
      this.MarketerOfChoice = results.data[LookupsEnum.MarketerOfChoice].lookupValues.filter(x => x.active === 'Y');
      this.MarketerOfChoice.sort((a, b) => (a.itemName > b.itemName) ? 1 : -1);
      this.MarketerOfChoiceUndecided = results.data[LookupsEnum.MarketerOfChoice].lookupValues.filter(x => x.active === 'Y');
      this.MarketerOfChoiceUndecided.sort((a, b) => (a.itemName > b.itemName) ? 1 : -1);
      this.bannerMessages = results.data[LookupsEnum.BannerMessages].lookupValues;
      this.developmentStreetType = results.data[LookupsEnum.DevelopmentStreetType].lookupValues;
      this.UtilityEasementType = results.data[LookupsEnum.UtilityEasementType].lookupValues;
      this.directions = results.data[LookupsEnum.Direction].lookupValues;
      this.serviceCounty = results.data[LookupsEnum.County].lookupValues;
      this.localDistrict = results.data[LookupsEnum.LocalDistrict].lookupValues;
      this.contractRejectReason = results.data[LookupsEnum.ContractRejectTeason].lookupValues;
      this.commonLookupsSource.next(true);
      this.commonLookupsLoaded = true;
    });
  }

  public loadBcaLookups(requestType: string, state: string) {
    this.bcaLookupsSource.next(false);
    this.fetchBcaLookups(requestType, state).subscribe(results => {
      this.loadType = results.data[LookupsEnum.LoadType].lookupValues;
      this.deliveryPressure = results.data[LookupsEnum.DeliveryPressure].lookupValues;
      this.defaultResidentialPressure = results.data[LookupsEnum.DefaultResidentialPressure];
      this.defaultCommercialPressure = results.data[LookupsEnum.DefaultCommercialPressure];
      this.gasLoadMultiplier = results.data[LookupsEnum.GasLoadMultiplier].lookupValues;
      this.wrLoadArmLkp = results.data[LookupsEnum.WRLoadARMLookup].lookupValues;
      this.buildingTypes = results.data[LookupsEnum.BuildingType].lookupValues;
      this.serviceCounty = results.data[LookupsEnum.County].lookupValues;
      this.bcaLookupsSource.next(true);
    });
  }
  public loadBuilderBcaLookups(requestType: string, state: string) {
    this.bcaLookupsSource.next(false);
    this.fetchBuilderBcaLookups(requestType, state).subscribe(results => {
      this.loadType = results.data[LookupsEnum.LoadType].lookupValues;
      this.deliveryPressure = results.data[LookupsEnum.DeliveryPressure].lookupValues;
      this.defaultResidentialPressure = results.data[LookupsEnum.DefaultResidentialPressure];
      this.defaultCommercialPressure = results.data[LookupsEnum.DefaultCommercialPressure];
      this.gasLoadMultiplier = results.data[LookupsEnum.GasLoadMultiplier].lookupValues;
      this.wrLoadArmLkp = results.data[LookupsEnum.WRLoadARMLookup].lookupValues;
      this.buildingTypes = results.data[LookupsEnum.BuildingType].lookupValues;
      this.serviceCounty = results.data[LookupsEnum.County].lookupValues;
      this.builderBcaLookupsSource.next(true);
    });
  }

  public resolveServiceAddressLookups(serviceAddress: any) {
    this.serviceAddressLookupsSource.next(false);
    this.fetchServiceAddressResolver(serviceAddress).subscribe(results => {
      this.serviceState = results.data[LookupsEnum.State].lookupValues;
      this.serviceCity = results.data[LookupsEnum.City].lookupValues;
      this.serviceCounty = results.data[LookupsEnum.County].lookupValues;
      this.meterDuplicate = results.data[LookupsEnum.MeterExist];
      this.bcaDuplicate = results.data[LookupsEnum.BcaDuplicate];
      this.serviceAddressLookupsSource.next(true);
    });
  }

  public resolveBuilderServiceAddressLookups(serviceAddress: any) {
    this.serviceAddressLookupsSource.next(false);
    this.fetchBuilderServiceAddressResolver(serviceAddress).subscribe(results => {
      this.serviceState = results.data[LookupsEnum.State].lookupValues;
      this.serviceCity = results.data[LookupsEnum.City].lookupValues;
      this.serviceCounty = results.data[LookupsEnum.County].lookupValues;
      this.meterDuplicate = results.data[LookupsEnum.MeterExist];
      this.serviceAddressLookupsSource.next(true);
    });
  }

  public resolveBCAFoundBuilder(serviceAddress: any) {
    this.fetchBCAFoundBuilder(serviceAddress).subscribe(results => {
      this.bcaDuplicate = results.data;
    });
  }

  public loadServiceAddressLookups(state: string, city: string, county: string) {
    this.serviceAddressLookupsSource.next(false);
     this.countyDetails = {cityName: city, countyName: county, stateName: state};
     this.fetchCityAndCountyByName(this.countyDetails).subscribe(results => {
      this.serviceState = results.data[LookupsEnum.State].lookupValues;
      this.serviceCity = results.data[LookupsEnum.City].lookupValues;
      this.serviceCounty = results.data[LookupsEnum.County].lookupValues;
      this.serviceAddressLookupsSource.next(true);
    });
  }

  public loadMailingAddressCityLookups(state: string, city: string) {
    this.mailingAddressLookupsSource.next(false);
    this.fetchCityByName(state, city).subscribe(results => {
      this.mailingCity = [results.data];
      this.mailingState = [this.states.find(x => x.id.toUpperCase() === state.toUpperCase())];
      this.mailingAddressLookupsSource.next(true);
    });
  }

  // HTTP Fetches
  private fetchCommonLookups(): Observable<ApiResult<EntityLookup>> {
    return this.socoHttp.get<EntityLookup>({
      url: 'Common/GetCommonLookups'
    });
  }

  private fetchBcaLookups(requestType: string, state: string): Observable<ApiResult<EntityLookup>> {
    return this.socoHttp.get<EntityLookup>({
      url: 'Common/GetBcaLookups',
      params: { 'requestType': requestType, 'state': state }
    });
  }

  private fetchBuilderBcaLookups(requestType: string, state: string): Observable<ApiResult<EntityLookup>> {
    return this.socoHttp.get<EntityLookup>({
      url: 'Common/GetBcaLookups',
      params: { 'requestType': requestType, 'state': state },
      pleaseWaitTimeoutDuration: 500,
      pleaseWaitMessage: this.pleaseWaitMsg,
      pleaseWaitContainerId: 'ContainerAwaiter'
    });
  }

  private fetchCityByName(state: string, city: string): Observable<ApiResult<City>> {
    return this.socoHttp.get<City>({
      url: 'Common/GetCityByName',
      params: { 'state': state, 'city': city }
    });
  }

   private fetchCityAndCountyByName(countyDetails: CountyDetailsModel): Observable<ApiResult<any>> {
     return this.socoHttp.get<any>({
       url: 'Common/ResolveCityCountyByName',
       params: countyDetails,
       shouldIgnoreInvalidResult: false,
       pleaseWaitTimeoutDuration: 500,
       pleaseWaitMessage: this.pleaseWaitMsg,
       pleaseWaitContainerId: 'ContainerAwaiter'
     });
  }
  private fetchServiceAddressResolver(serviceAddress: any): Observable<ApiResult<any>> {
    return this.socoHttp.get<any>({
      url: 'Applicant/ResolveServiceAddress',
      params: serviceAddress,
      pleaseWaitTimeoutDuration: 500,
      pleaseWaitMessage: this.pleaseWaitMsg,
      pleaseWaitContainerId: 'ContainerAwaiter'
    });
  }

  private fetchBuilderServiceAddressResolver(serviceAddress: any): Observable<ApiResult<any>> {
    return this.socoHttp.get<any>({
      url: 'BuilderLookups/ResolveServiceAddress',
      params: serviceAddress,
      pleaseWaitTimeoutDuration: 500,
      pleaseWaitMessage: this.pleaseWaitMsg,
      pleaseWaitContainerId: 'ContainerAwaiter'
    });
  }

  private fetchBCAFoundBuilder(serviceAddress: any): Observable<ApiResult<any>> {
    return this.socoHttp.get<any>({
      url: 'BuilderLookups/BCAFound',
      params: serviceAddress,
      pleaseWaitTimeoutDuration: 500,
      pleaseWaitMessage: this.pleaseWaitMsg,
      pleaseWaitContainerId: 'ContainerAwaiter'
    });
  }

 
   GetGoogleAnalyticsAccount(): Observable<ApiResult<any>> {
    return this.socoHttp.get<any[]>({
      url: 'applicant/GasGoogleAnalytics'
    });
  }
}
